import { useState, useMemo } from "react";
import { useNestedTabState } from "@/hooks/use-nested-tab-state";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Plus, Eye, Pencil, ChevronLeft, Archive } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { FacilityFormModal } from "./facility-form-modal";
import {
  FacilityOverviewTab,
  FacilityFinancialTab,
  FacilityPartiesTab,
  FacilityDocumentsTab,
} from "./facilities/tabs";
import { FacilitySecuritiesPanel } from "./facilities/facility-securities-panel";
import type { Facility, Lender, Deal } from "@shared/schema";

const STATUS_COLORS: Record<string, string> = {
  indicative: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200",
  submitted: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  approved: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  settled: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200",
  declined: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
  withdrawn: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200",
  scenario_not_proceeding: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
};

function formatCurrency(value: string | null | undefined): string {
  if (!value) return "-";
  const num = parseFloat(value);
  if (isNaN(num)) return "-";
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
}

interface FacilitiesPanelProps {
  dealId: string;
  clientGroupId?: string;
}

/**
 * FacilitiesPanel - Nested Tab Pattern (RULE-TAB-012)
 *
 * This component implements the nested tab pattern for Facilities within a Deal:
 * - VIEW 1: Facilities List (selector view with table of facilities)
 * - VIEW 2: Selected Facility Detail with sub-tabs (Overview, Financial, Parties, Securities, Documents)
 *
 * Pattern Reference: Similar to FacilitySecuritiesPanel which implements nested tabs for Securities.
 */
export function FacilitiesPanel({ dealId, clientGroupId }: FacilitiesPanelProps) {
  // NESTED TAB STATE: URL-persisted via hash (#facilities/{id}/{subtab})
  const {
    selectedId: selectedFacilityId,
    subTab: facilityDetailTab,
    setSelectedId: setSelectedFacilityId,
    setSubTab: setFacilityDetailTab,
    clearSelection,
  } = useNestedTabState({ parentTab: "facilities", defaultSubTab: "overview" });

  // Form/modal state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingFacility, setEditingFacility] = useState<Facility | undefined>();
  const [showArchived, setShowArchived] = useState(false);

  const { toast } = useToast();

  // Fetch active facilities for this deal (default view)
  const { data: activeFacilities = [], isLoading } = useQuery<Facility[]>({
    queryKey: ["/api/crm/deals", dealId, "facilities"],
    enabled: !!dealId,
  });

  // Fetch ALL facilities including archived (for toggle count and display)
  const { data: allFacilities = [] } = useQuery<Facility[]>({
    queryKey: ["/api/crm/deals", dealId, "facilities", { includeScenarios: "true" }],
    enabled: !!dealId,
  });

  // Compute archived facilities count
  const archivedFacilities = useMemo(() => {
    return allFacilities.filter(f => f.facilityStatus === 'scenario_not_proceeding');
  }, [allFacilities]);

  const archivedCount = archivedFacilities.length;

  // Determine which facilities to display based on toggle state
  const facilities = showArchived ? allFacilities : activeFacilities;

  // Fetch lenders for name lookup
  const { data: lenders = [] } = useQuery<Lender[]>({
    queryKey: ["/api/crm/lenders"],
  });

  // Fetch parent deal info
  const { data: deal } = useQuery<Deal>({
    queryKey: ["/api/crm/deals", dealId],
    enabled: !!dealId,
  });

  // Fetch selected facility details
  const { data: selectedFacility, isLoading: isLoadingSelected } = useQuery<Facility>({
    queryKey: ["/api/crm/facilities", selectedFacilityId],
    enabled: !!selectedFacilityId,
  });

  // Create lender name lookup map
  const lenderMap = new Map<string, string>(
    lenders.map((lender) => [lender.id, lender.lenderName])
  );

  const deleteMutation = useMutation({
    mutationFn: async (facilityId: string) => {
      await apiRequest("DELETE", `/api/crm/facilities/${facilityId}`);
      return facilityId;
    },
    onSuccess: (_data, deletedFacilityId) => {
      queryClient.invalidateQueries({
        queryKey: ["/api/crm/deals", dealId, "facilities"],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/crm/facilities"] });
      // ISSUE-021 Rework #1: Invalidate specific deal to trigger auto-refresh of Financials tab
      queryClient.invalidateQueries({ queryKey: ["/api/crm/deals", dealId] });
      toast({
        title: "Facility removed",
        description: "The facility has been successfully deleted.",
      });
      // If deleted the selected facility, go back to list
      if (deletedFacilityId === selectedFacilityId) {
        clearSelection();
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete facility",
        variant: "destructive",
      });
    },
  });

  const handleEdit = (facility: Facility) => {
    setEditingFacility(facility);
    setIsFormOpen(true);
  };

  const handleAddNew = () => {
    setEditingFacility(undefined);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingFacility(undefined);
  };

  const handleRowClick = (facilityId: string) => {
    setSelectedFacilityId(facilityId); // Resets sub-tab to "overview" automatically
  };

  const handleBackToList = () => {
    clearSelection();
  };

  // Determine if toggle should be visible:
  // 1. Deal must be in 'deal' stage (not 'scenario' or 'funded_loan')
  // 2. There must be at least one archived facility
  const showToggle = deal?.transactionStage === 'deal' && archivedCount > 0;

  // ============================================================================
  // VIEW 1: Facilities List (Selector View)
  // ============================================================================
  if (!selectedFacilityId) {
    return (
      <>
        <Card data-testid="panel-facilities">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-base font-medium">Facilities</CardTitle>
            <div className="flex gap-2">
              {showToggle && (
                <Button
                  size="sm"
                  variant={showArchived ? "default" : "outline"}
                  onClick={() => setShowArchived(!showArchived)}
                  data-testid="toggle-archived-scenarios"
                >
                  <Archive className="h-4 w-4 mr-1" />
                  {showArchived ? (
                    "Hide Archived Scenarios"
                  ) : (
                    <>
                      Show Archived Scenarios{" "}
                      <span data-testid="badge-archived-count">({archivedCount})</span>
                    </>
                  )}
                </Button>
              )}
              <Button size="sm" onClick={handleAddNew}>
                <Plus className="h-4 w-4 mr-1" />
                Add Facility
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8 text-sm text-muted-foreground">
                Loading facilities...
              </div>
            ) : facilities.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-sm text-muted-foreground mb-3">
                  No facilities added to this deal yet
                </p>
                <Button size="sm" variant="outline" onClick={handleAddNew}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add First Facility
                </Button>
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Facility Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Lender</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Requested</TableHead>
                      <TableHead className="text-right">Approved</TableHead>
                      <TableHead className="text-right">Settled</TableHead>
                      <TableHead className="w-[120px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {facilities.map((facility) => {
                      const isArchived = facility.facilityStatus === 'scenario_not_proceeding';
                      const rowTestId = isArchived
                        ? `row-facility-${facility.id}-archived`
                        : `row-facility-${facility.id}`;

                      return (
                        <TableRow
                          key={facility.id}
                          data-testid={rowTestId}
                          className={`cursor-pointer hover:bg-muted/50 ${
                            isArchived ? 'opacity-60 bg-gray-50' : ''
                          }`}
                          onClick={() => handleRowClick(facility.id)}
                        >
                          <TableCell className="font-medium">
                            {isArchived ? (
                              <span>{facility.facilityName}</span>
                            ) : (
                              <button
                                type="button"
                                className="text-left hover:underline hover:text-primary focus:outline-none"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEdit(facility);
                                }}
                              >
                                {facility.facilityName}
                              </button>
                            )}
                          </TableCell>
                          <TableCell>{facility.facilityType || "-"}</TableCell>
                          <TableCell>
                            {facility.lenderId ? lenderMap.get(facility.lenderId) || "-" : "-"}
                          </TableCell>
                          <TableCell>
                            {isArchived ? (
                              <Badge
                                variant="secondary"
                                data-testid="badge-not-proceeding"
                                className="capitalize text-xs bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200"
                              >
                                Not Proceeding
                              </Badge>
                            ) : (
                              <Badge
                                variant="secondary"
                                className={`capitalize text-xs ${
                                  STATUS_COLORS[facility.facilityStatus || "indicative"]
                                }`}
                              >
                                {(facility.facilityStatus || "indicative").replace("_", " ")}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(facility.requestedAmount)}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(facility.approvedLimit)}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(facility.fundedAmount)}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRowClick(facility.id)}
                                title="View details"
                                data-testid={`button-view-facility-${facility.id}`}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              {!isArchived && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEdit(facility)}
                                  title="Edit"
                                  data-testid={`button-edit-facility-${facility.id}`}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <FacilityFormModal
          isOpen={isFormOpen}
          onClose={handleCloseForm}
          dealId={dealId}
          clientGroupId={deal?.clientGroupId ?? undefined}
          facility={editingFacility}
          onDelete={(facilityId) => {
            deleteMutation.mutate(facilityId);
            handleCloseForm();
          }}
          defaultBorrowerId={deal?.borrowerId ?? undefined}
          defaultBorrowerType={(deal?.borrowerType as "account" | "contact") ?? undefined}
        />
      </>
    );
  }

  // ============================================================================
  // VIEW 2: Facility Detail with Nested Tabs (RULE-TAB-012 Compliance)
  // Sub-Tabs: Overview, Financial, Parties, Securities, Documents
  // ============================================================================
  return (
    <div className="space-y-4">
      {/* Back Button */}
      <div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleBackToList}
          className="mb-2"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back to Facilities List
        </Button>
      </div>

      {/* Facility Header */}
      {isLoadingSelected ? (
        <div className="text-center py-8">
          <p className="text-sm text-muted-foreground">Loading facility details...</p>
        </div>
      ) : selectedFacility ? (
        <>
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-semibold">{selectedFacility.facilityName}</h3>
              <div className="flex items-center gap-2 mt-1">
                <Badge
                  variant="secondary"
                  className={`capitalize text-xs ${
                    STATUS_COLORS[selectedFacility.facilityStatus || "indicative"]
                  }`}
                >
                  {(selectedFacility.facilityStatus || "indicative").replace("_", " ")}
                </Badge>
                <span className="text-sm text-muted-foreground">
                  {selectedFacility.facilityType || "No type"} • {formatCurrency(selectedFacility.approvedLimit)}
                </span>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleEdit(selectedFacility)}
              >
                <Pencil className="h-4 w-4 mr-1" />
                Edit
              </Button>
            </div>
          </div>

          {/* Nested Tabs for Facility Detail */}
          <Tabs value={facilityDetailTab} onValueChange={setFacilityDetailTab}>
            <TabsList className="w-full justify-start">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="financial">Financial</TabsTrigger>
              <TabsTrigger value="parties">Parties</TabsTrigger>
              <TabsTrigger value="securities">Securities</TabsTrigger>
              <TabsTrigger value="documents">Documents</TabsTrigger>
            </TabsList>

            <TabsContent value="overview">
              <FacilityOverviewTab
                facility={selectedFacility}
                deal={deal}
                dealId={dealId}
              />
            </TabsContent>

            <TabsContent value="financial">
              <FacilityFinancialTab
                facility={selectedFacility}
                dealId={dealId}
              />
            </TabsContent>

            <TabsContent value="parties">
              <FacilityPartiesTab
                facility={selectedFacility}
                dealId={dealId}
                clientGroupId={clientGroupId}
              />
            </TabsContent>

            <TabsContent value="securities">
              <FacilitySecuritiesPanel
                facilityId={selectedFacility.id}
                facilityLimit={selectedFacility.approvedLimit}
                facilityName={selectedFacility.facilityName}
                clientGroupId={clientGroupId}
              />
            </TabsContent>

            <TabsContent value="documents">
              <FacilityDocumentsTab
                facility={selectedFacility}
                dealId={dealId}
              />
            </TabsContent>
          </Tabs>

          {/* Modals for this view */}
          <FacilityFormModal
            isOpen={isFormOpen}
            onClose={handleCloseForm}
            dealId={dealId}
            clientGroupId={deal?.clientGroupId ?? undefined}
            facility={editingFacility}
            onDelete={(facilityId) => {
              deleteMutation.mutate(facilityId);
              handleCloseForm();
            }}
            defaultBorrowerId={deal?.borrowerId ?? undefined}
            defaultBorrowerType={(deal?.borrowerType as "account" | "contact") ?? undefined}
          />
        </>
      ) : (
        <div className="text-center py-8">
          <p className="text-sm text-muted-foreground">Facility not found</p>
          <Button
            variant="outline"
            size="sm"
            onClick={handleBackToList}
            className="mt-2"
          >
            Back to Facilities List
          </Button>
        </div>
      )}
    </div>
  );
}
